import { useState, useRef, useEffect, FormEvent } from "react";
import ReactMarkdown from "react-markdown";
import { Send, Bot, User, Loader2, Copy, Download, Check, Brain, ChevronDown, ThumbsUp, ThumbsDown } from "lucide-react";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

import { supabase } from "@/integrations/supabase/client";

export type ChatMsg = { role: "user" | "assistant"; content: string; reasoning?: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openrouter-chat`;
const FOLLOWUP_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/followup-suggestions`;

// Daily limit removed — model is now free via OpenRouter


interface DeepseekChatProps {
  /** "full" = dedicated chat page, "compact" = embedded hero on homepage */
  variant?: "full" | "compact";
  placeholder?: string;
  suggestions?: string[];
  emptyHeadline?: string;
  emptySubline?: string;
  className?: string;
  heightClass?: string;
}

const MessageActions = ({ content, index, userMessage }: { content: string; index: number; userMessage?: string }) => {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<"up" | "down" | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFeedback = async (value: "up" | "down") => {
    if (submitting) return;
    const next = feedback === value ? null : value;
    setFeedback(next);
    if (!next) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.from("chat_feedback").insert({
        rating: next,
        assistant_message: content,
        user_message: userMessage ?? null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      if (error) throw error;
      toast({
        title: next === "up" ? "Thanks for the feedback!" : "Thanks — we'll do better",
        description:
          next === "up"
            ? "Glad this response was helpful."
            : "Your feedback helps us improve future answers.",
      });
    } catch (err) {
      console.error("feedback insert error:", err);
      setFeedback(null);
      toast({ title: "Couldn't save feedback", description: "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast({ title: "Copied to clipboard" });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const handlePdf = () => {
    try {
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 48;
      const maxWidth = pageWidth - margin * 2;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.setTextColor(0, 102, 255);
      pdf.text("DeepSeek AI Response", margin, margin);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(
        `Generated ${new Date().toLocaleString()} via deepseek.ai (DeepSeek V3.1, free community route)`,
        margin,
        margin + 16
      );

      pdf.setFontSize(11);
      pdf.setTextColor(20, 20, 20);
      const lines = pdf.splitTextToSize(content, maxWidth);
      let y = margin + 44;
      const lineHeight = 15;
      for (const line of lines) {
        if (y > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }
        pdf.text(line, margin, y);
        y += lineHeight;
      }

      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        "Independent fan site — not affiliated with DeepSeek.com",
        margin,
        pageHeight - 24
      );

      pdf.save(`deepseek-response-${index + 1}.pdf`);
      toast({ title: "PDF downloaded" });
    } catch (e) {
      console.error(e);
      toast({ title: "PDF export failed", variant: "destructive" });
    }
  };

  return (
    <div className="flex gap-1 mt-1.5 ml-1">
      <button
        onClick={handleCopy}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0066FF] px-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
        aria-label="Copy response"
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
      <button
        onClick={handlePdf}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0066FF] px-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
        aria-label="Download as PDF"
      >
        <Download className="h-3 w-3" />
        PDF
      </button>
      <div className="mx-1 w-px bg-slate-200" aria-hidden="true" />
      <button
        onClick={() => handleFeedback("up")}
        className={cn(
          "inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors",
          feedback === "up"
            ? "text-[#0066FF] bg-[#0066FF]/10"
            : "text-slate-500 hover:text-[#0066FF] hover:bg-slate-100"
        )}
        aria-label="Good response"
        aria-pressed={feedback === "up"}
      >
        <ThumbsUp className="h-3 w-3" />
      </button>
      <button
        onClick={() => handleFeedback("down")}
        className={cn(
          "inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors",
          feedback === "down"
            ? "text-red-600 bg-red-50"
            : "text-slate-500 hover:text-red-600 hover:bg-slate-100"
        )}
        aria-label="Bad response"
        aria-pressed={feedback === "down"}
      >
        <ThumbsDown className="h-3 w-3" />
      </button>
    </div>
  );
};

const ReasoningBlock = ({ reasoning, streaming }: { reasoning: string; streaming: boolean }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-2 rounded-xl border border-slate-200 bg-slate-50/70 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <Brain className="h-3.5 w-3.5 text-[#0066FF]" />
        <span>{streaming ? "Thinking…" : "Thought process"}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 ml-auto transition-transform", open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 text-xs text-slate-600 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto font-mono">
          {reasoning}
        </div>
      )}
    </div>
  );
};

const DeepseekChat = ({
  variant = "full",
  placeholder = "Message DeepSeek V3.1…",
  suggestions = [],
  emptyHeadline = "How can I help you today?",
  emptySubline = "Ask anything — code, math, writing, analysis.",
  className,
  heightClass,
}: DeepseekChatProps) => {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [followups, setFollowups] = useState<string[]>([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, followups]);

  const send = async (text: string) => {
    if (!text.trim() || isLoading) return;

    // Fire GA4 chat_started on the first user message of the session
    if (messages.filter((m) => m.role === "user").length === 0) {
      import("@/lib/analytics").then(({ trackEvent }) =>
        trackEvent("chat_started", { prompt_length: text.trim().length })
      );
    }

    const userMsg: ChatMsg = { role: "user", content: text.trim() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setIsLoading(true);
    setFollowups([]);
    setFollowupsLoading(false);

    const OFFICIAL_CHAT_LINE =
      "In the meantime, use the [**official DeepSeek chat**](https://chat.deepseek.com) " +
      "(V4-Pro and V4-Flash, free account), or our " +
      "[**free Chrome sidebar**](https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop).";

    const RATE_LIMIT_MSG =
      "⚠️ **The free community route is full right now.**\n\n" +
      "This demo runs on free routes with a daily cap, and it has been hit. Try again in a minute or later today.\n\n" +
      OFFICIAL_CHAT_LINE;

    const FALLBACK_MSG =
      "⚠️ **Our free demo chat is temporarily unavailable.**\n\n" +
      "The free community route did not respond. Please try again shortly.\n\n" +
      OFFICIAL_CHAT_LINE;

    const pushFallback = () => {
      setMessages((prev) => [...prev, { role: "assistant", content: FALLBACK_MSG }]);
    };

    let assistantSoFar = "";
    let reasoningSoFar = "";
    const upsert = (chunk: string, kind: "content" | "reasoning" = "content") => {
      if (kind === "reasoning") reasoningSoFar += chunk;
      else assistantSoFar += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        const updated = { role: "assistant" as const, content: assistantSoFar, reasoning: reasoningSoFar || undefined };
        if (last?.role === "assistant") {
          return prev.map((m, i) => (i === prev.length - 1 ? updated : m));
        }
        return [...prev, updated];
      });
    };

    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: next }),
      });

      if (!resp.ok) {
        let errorMessage = resp.status === 429 ? RATE_LIMIT_MSG : FALLBACK_MSG;
        try {
          const data = await resp.json();
          if (resp.status === 429 && typeof data?.error === "string") {
            errorMessage = `⚠️ **The free community route is full right now.**\n\n${data.error}\n\n${OFFICIAL_CHAT_LINE}`;
          }
        } catch {
          // Keep the default user-facing message.
        }
        setMessages((prev) => [...prev, { role: "assistant", content: errorMessage }]);
        setIsLoading(false);
        return;
      }

      if (!resp.body) throw new Error("No response body");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;

      while (!done) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line || line.startsWith(":")) continue;
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") {
            done = true;
            break;
          }
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta;
            const reasoning = delta?.reasoning ?? delta?.reasoning_content;
            if (reasoning) upsert(reasoning, "reasoning");
            if (delta?.content) upsert(delta.content, "content");
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }
    } catch (e) {
      console.error(e);
      pushFallback();
    } finally {
      setIsLoading(false);
      // Fetch follow-up suggestions once we have an assistant answer
      if (assistantSoFar.trim().length > 0) {
        setFollowupsLoading(true);
        try {
          const fr = await fetch(FOLLOWUP_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({
              messages: [...next, { role: "assistant", content: assistantSoFar }],
            }),
          });
          if (fr.ok) {
            const data = await fr.json();
            if (Array.isArray(data?.suggestions)) setFollowups(data.suggestions);
          }
        } catch (err) {
          console.error("followup fetch error:", err);
        } finally {
          setFollowupsLoading(false);
        }
      }
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const defaultHeight = variant === "compact" ? "h-[420px]" : "h-[70vh]";

  return (
    <div
      className={cn(
        "bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col",
        heightClass ?? defaultHeight,
        className
      )}
    >
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 rounded-2xl bg-[#0066FF] flex items-center justify-center mb-3">
              <Bot className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-base md:text-lg font-semibold text-slate-900">{emptyHeadline}</h2>
            <p className="text-sm text-slate-500 mt-1">{emptySubline}</p>
            {suggestions.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5 w-full max-w-xl">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-left text-sm px-4 py-3 border border-slate-200 rounded-xl hover:border-[#0066FF] hover:bg-[#0066FF]/5 transition-colors text-slate-700"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={cn("flex gap-3", m.role === "user" ? "justify-end" : "justify-start")}
          >
            {m.role === "assistant" && (
              <div className="w-8 h-8 rounded-full bg-[#0066FF] flex items-center justify-center flex-shrink-0">
                <Bot className="h-4 w-4 text-white" />
              </div>
            )}
            <div className="flex flex-col max-w-[80%]">
              {m.role === "assistant" && m.reasoning && (
                <ReasoningBlock reasoning={m.reasoning} streaming={isLoading && i === messages.length - 1 && !m.content} />
              )}
              <div
                className={cn(
                  "rounded-2xl px-4 py-3 text-sm",
                  m.role === "user"
                    ? "bg-[#0066FF] text-white"
                    : "bg-slate-100 text-slate-900",
                  m.role === "assistant" && !m.content && "hidden"
                )}
              >
                {m.role === "assistant" ? (
                  <div className="prose prose-sm max-w-none prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-code:text-[#0066FF] prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:code:!text-slate-100 prose-pre:code:!bg-transparent prose-pre:code:!p-0">
                    <ReactMarkdown>{m.content || "…"}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{m.content}</p>
                )}
              </div>
              {m.role === "assistant" && m.content && !(isLoading && i === messages.length - 1) && (
                <MessageActions content={m.content} index={i} userMessage={messages[i - 1]?.role === "user" ? messages[i - 1].content : undefined} />
              )}
            </div>
            {m.role === "user" && (
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-slate-700" />
              </div>
            )}
          </div>
        ))}

        {isLoading && messages[messages.length - 1]?.role === "user" && (
          <div className="flex gap-3 justify-start">
            <div className="w-8 h-8 rounded-full bg-[#0066FF] flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div className="bg-slate-100 rounded-2xl px-4 py-3">
              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
            </div>
          </div>
        )}

        {!isLoading && followups.length > 0 && messages[messages.length - 1]?.role === "assistant" && (
          <div className="pl-11 pt-1">
            <p className="text-xs text-slate-400 mb-2">Suggested follow-ups</p>
            <div className="flex flex-wrap gap-2">
              {followups.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="text-left text-xs px-3 py-1.5 border border-slate-200 rounded-full hover:border-[#0066FF] hover:bg-[#0066FF]/5 hover:text-[#0066FF] transition-colors text-slate-700"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {followupsLoading && !isLoading && (
          <div className="pl-11 pt-1 flex items-center gap-2 text-xs text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" />
            Generating follow-up questions…
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="border-t border-slate-200 p-3 md:p-4">
        <div className="flex gap-2 items-end">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
            className="min-h-[52px] max-h-40 resize-none rounded-xl"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e as unknown as FormEvent);
              }
            }}
            disabled={isLoading}
          />
          <Button
            type="submit"
            aria-label="Send message"
            disabled={isLoading || !input.trim()}
            className="h-[52px] px-4 rounded-xl bg-[#0066FF] hover:bg-[#0066FF]/90"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2 text-center">
          Independent fan site — not affiliated with DeepSeek.com. This demo answers with DeepSeek V3.1 on free
          community routes (with R1 as fallback when a route is busy). For V4-Flash and V4-Pro, use the official
          chat at chat.deepseek.com.
        </p>
      </form>
    </div>
  );
};

export default DeepseekChat;
